import React, { useState, useEffect, useMemo } from 'react';
import { Select, Button, Skeleton, Tooltip } from 'antd';
import {
  CloseOutlined,
  FileTextOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useSetAtom, useAtomValue } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import moment from 'moment';
import { useListReportVersions, useGetReportVersion } from '../hooks/useReportApi';
import { useChatStream } from '../hooks/useChatStream';
import { useDownloadReport } from '../hooks/useDownloadReport';
import apptracker from '@src/utils/sensor/apptracker';
import { NEXA_REPORT_DOWNLOAD } from '@src/utils/sensor/track';
import {
  isReportPanelOpenAtom,
  getSessionId,
  isSharedView,
  activeReportVersionAtom,
  isReportGeneratingAtom,
  chatStreamLoadingAtom,
  reportStreamPhaseDerivedAtom,
  reportStreamVersionDerivedAtom,
  reportStreamMessageDerivedAtom,
  reportStreamHtmlDerivedAtom,
} from '../NexaStore';
import { clearReportStreamAtom } from '../NexaHandlers';
import {
  ReportPanelWrapper,
  ReportHeader,
  ReportHeaderLeft,
  ReportHeaderRight,
  ReportContent,
  ReportIframe,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
  LoadingContainer,
  LoadingMessage,
  SkeletonWrapper,
  StreamingOverlay,
  StreamingOverlayMessage,
} from './styled';

type LoadingType = 'fetching' | 'generating' | null;

export const ReportPanel: React.FC = () => {
  const sessionId = getSessionId();
  const setIsReportPanelOpen = useSetAtom(isReportPanelOpenAtom);
  const isReportPanelOpen = useAtomValue(isReportPanelOpenAtom);
  const activeVersion = useAtomValue(activeReportVersionAtom);
  const setActiveVersion = useSetAtom(activeReportVersionAtom);
  const isReportGenerating = useAtomValue(isReportGeneratingAtom);
  const reportStreamPhase = useAtomValue(reportStreamPhaseDerivedAtom);
  const reportStreamVersion = useAtomValue(reportStreamVersionDerivedAtom);
  const reportStreamMessage = useAtomValue(reportStreamMessageDerivedAtom);
  const reportStreamHtml = useAtomValue(reportStreamHtmlDerivedAtom);
  const clearReportStreamState = useSetAtom(clearReportStreamAtom);
  const isChatLoading = useAtomValue(chatStreamLoadingAtom);
  const queryClient = useQueryClient();
  const { fetchStream } = useChatStream();
  const { downloadImage, isDownloading } = useDownloadReport();

  // Auto-detect if this is a shared view
  const isShared = isSharedView();

  // Hooks automatically use correct endpoint based on isSharedView()
  const {
    versions,
    generationInProgress,
    displayName,
    isFetching: isFetchingVersions,
  } = useListReportVersions();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const { htmlContent, isFetching: isFetchingVersion } = useGetReportVersion(activeVersion);

  // Invalidate and refetch versions when panel opens
  useEffect(() => {
    if (isReportPanelOpen && sessionId) {
      queryClient.invalidateQueries({
        queryKey: ['nexa-report-versions', sessionId],
      });
    }
  }, [isReportPanelOpen, sessionId, queryClient]);

  // Auto-select latest version when versions load
  useEffect(() => {
    if (versions.length > 0 && !activeVersion) {
      setActiveVersion(versions[0].version);
    }
  }, [versions, activeVersion, setActiveVersion]);

  // Derive loading type directly to avoid useEffect render-gap flicker.
  const loadingType: LoadingType = isFetchingVersion
    ? 'fetching'
    : isReportGenerating
      ? 'generating'
      : null;

  const handleClose = () => {
    setIsFullscreen(false);
    clearReportStreamState();
    setIsReportPanelOpen(false);
  };

  const handleVersionChange = (versionId: number) => {
    setActiveVersion(versionId);
  };

  const handleGenerateReport = () => {
    if (!sessionId) return;
    clearReportStreamState();
    fetchStream({ query: 'generate a report' });
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleDownloadImage = () => {
    if (!sessionId || !activeVersion) return;
    apptracker(NEXA_REPORT_DOWNLOAD, { id: sessionId, version: activeVersion });
    downloadImage(sessionId, activeVersion, displayName || undefined);
  };

  const hasPreviewBuffer = Boolean(reportStreamHtml);
  const hasTargetVersion = typeof reportStreamVersion === 'number';
  const isTargetVersionSelected = hasTargetVersion && activeVersion === reportStreamVersion;
  const isPersistedTargetReady =
    hasPreviewBuffer && isTargetVersionSelected && !isFetchingVersion && Boolean(htmlContent);

  // Keep streamed preview active until the exact target version has been loaded.
  const shouldRenderStreamPreview = hasPreviewBuffer && !isPersistedTargetReady;
  const isTransitioningToPersisted = shouldRenderStreamPreview && !isReportGenerating;

  useEffect(() => {
    if (isPersistedTargetReady) {
      clearReportStreamState();
    }
  }, [clearReportStreamState, isPersistedTargetReady]);

  const renderedHtml = useMemo(() => {
    if (!shouldRenderStreamPreview) {
      return htmlContent;
    }

    return reportStreamHtml || '';
  }, [htmlContent, reportStreamHtml, shouldRenderStreamPreview]);

  // Sanitize HTML content with DOMPurify
  const sanitizedHtml = useMemo(() => {
    if (!renderedHtml) return '';

    return DOMPurify.sanitize(renderedHtml, {
      WHOLE_DOCUMENT: true,
      ADD_TAGS: ['style', 'link', 'meta'],
      ADD_ATTR: ['target', 'href', 'rel', 'crossorigin'],
      ALLOW_DATA_ATTR: true,
      FORCE_BODY: false,
    });
  }, [renderedHtml]);

  // Format version options for dropdown
  const versionOptions = useMemo(() => {
    const options = versions.map((v) => {
      const formattedDate = moment(v.created_at).format('MMM D, YYYY h:mm A');
      return {
        value: v.version,
        label: `v${v.version} (${formattedDate})`,
      };
    });

    // If we have an active version but it's not in the loaded versions (race condition),
    // add a placeholder option to prevent showing raw value
    if (activeVersion && !options.find((opt) => opt.value === activeVersion)) {
      options.unshift({
        value: activeVersion,
        label: `v${activeVersion} (Loading...)`,
      });
    }

    return options;
  }, [versions, activeVersion]);

  // Determine state and content.
  // Include isReportGenerating so the panel stays in loading state (not empty)
  // while the backend is still generating and no versions exist yet.
  const isInitialLoading =
    (isFetchingVersions || generationInProgress || (isReportGenerating && !hasPreviewBuffer)) &&
    versions.length === 0;
  const isEmpty =
    versions.length === 0 &&
    !isFetchingVersions &&
    !generationInProgress &&
    !isReportGenerating &&
    !activeVersion;
  const isOperationLoading = !!loadingType;
  const shouldShowLoading = isInitialLoading || (isOperationLoading && !shouldRenderStreamPreview);
  const hasVersions = versions.length > 0 || !!activeVersion;

  // Render header based on state
  const renderHeader = () => (
    <ReportHeader>
      <ReportHeaderLeft>
        {hasVersions && (
          <Select
            value={activeVersion}
            onChange={handleVersionChange}
            options={versionOptions}
            style={{ minWidth: 200 }}
            disabled={isOperationLoading}
          />
        )}
      </ReportHeaderLeft>
      <ReportHeaderRight>
        {hasVersions && (
          <Tooltip title="Download as image">
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadImage}
              loading={isDownloading}
              disabled={!activeVersion || isOperationLoading}
            />
          </Tooltip>
        )}
        {hasVersions && !isShared && (
          <Button
            type="primary"
            onClick={handleGenerateReport}
            disabled={isOperationLoading || isChatLoading}
          >
            Generate New Report
          </Button>
        )}
        <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
          <Button
            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={handleToggleFullscreen}
          />
        </Tooltip>
        <Tooltip title="Close">
          <Button icon={<CloseOutlined />} onClick={handleClose} />
        </Tooltip>
      </ReportHeaderRight>
    </ReportHeader>
  );

  // Render content based on state
  const renderContent = () => {
    // Loading states (initial or during operations)
    if (shouldShowLoading) {
      const message =
        loadingType === 'generating'
          ? reportStreamMessage || 'Generating your report — this may take up to a minute.'
          : 'Loading reports...';

      return (
        <ReportContent>
          <LoadingContainer>
            <SkeletonWrapper>
              <Skeleton active paragraph={{ rows: 8 }} />
            </SkeletonWrapper>
            <LoadingMessage>{message}</LoadingMessage>
          </LoadingContainer>
        </ReportContent>
      );
    }

    // Empty state
    if (isEmpty) {
      return (
        <ReportContent>
          <EmptyStateContainer>
            <EmptyStateIcon>
              <FileTextOutlined />
            </EmptyStateIcon>
            <EmptyStateTitle>No reports available</EmptyStateTitle>
            <EmptyStateDescription>
              {isShared
                ? 'No reports were generated for this shared session'
                : 'Generate your first report to get started'}
            </EmptyStateDescription>
            {!isShared && (
              <Button
                type="primary"
                onClick={handleGenerateReport}
                size="large"
                disabled={isChatLoading}
              >
                Generate Report
              </Button>
            )}
          </EmptyStateContainer>
        </ReportContent>
      );
    }

    // Display report
    return (
      <ReportContent className="color-critical">
        {sanitizedHtml && (
          <ReportIframe srcDoc={sanitizedHtml} sandbox="allow-same-origin" title="Report Content" />
        )}
        {(isReportGenerating || isTransitioningToPersisted) && (
          <StreamingOverlay>
            <StreamingOverlayMessage>
              {isReportGenerating
                ? reportStreamMessage || 'Generating report content...'
                : reportStreamPhase === 'done'
                  ? isTargetVersionSelected
                    ? 'Refreshing saved report...'
                    : 'Finalizing and syncing saved report...'
                  : reportStreamMessage || 'Refreshing saved report...'}
            </StreamingOverlayMessage>
          </StreamingOverlay>
        )}
      </ReportContent>
    );
  };

  return (
    <ReportPanelWrapper $isFullscreen={isFullscreen}>
      {renderHeader()}
      {renderContent()}
    </ReportPanelWrapper>
  );
};

export default ReportPanel;
